/**
 * URL state for `/reports/incoming` (Order 31C).
 * Same Organization-timezone YYYY-MM-DD date semantics as Outgoing.
 */
import {
  DEFAULT_REPORT_MESSAGE_PAGE_SIZE,
  DEFAULT_REPORT_MESSAGE_SORT,
  MESSAGE_TYPES,
  PLATFORM_TYPES,
  REPORT_MESSAGE_PAGE_SIZES,
  REPORT_MESSAGE_SORTS,
  type MessageTypeName,
  type PlatformTypeName,
  type ReportMessagePageSize,
  type ReportMessageSortName,
} from "@multi-chat/shared";

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

export type IncomingReportQuery = {
  from?: string;
  to?: string;
  search: string;
  platform?: PlatformTypeName;
  endpointId?: number;
  messageType?: MessageTypeName;
  page: number;
  pageSize: ReportMessagePageSize;
  sort: ReportMessageSortName;
};

function parseYmd(raw: string | null): string | null {
  if (raw == null || !YMD_RE.test(raw)) return null;
  const parts = raw.split("-").map(Number);
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (y == null || m == null || d == null) return null;
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return raw;
}

function parsePositiveInt(raw: string | null): number | undefined {
  if (raw == null || raw.trim() === "") return undefined;
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < 1) return undefined;
  return parsed;
}

function isPlatform(value: string | null): value is PlatformTypeName {
  return value != null && (PLATFORM_TYPES as readonly string[]).includes(value);
}

function isMessageType(value: string | null): value is MessageTypeName {
  return value != null && (MESSAGE_TYPES as readonly string[]).includes(value);
}

function isSort(value: string | null): value is ReportMessageSortName {
  return value != null && (REPORT_MESSAGE_SORTS as readonly string[]).includes(value);
}

export function parseIncomingReportQuery(search: string): IncomingReportQuery {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const from = parseYmd(params.get("from"));
  const to = parseYmd(params.get("to"));
  const pageSizeRaw = Number(params.get("pageSize"));
  const pageSize = (REPORT_MESSAGE_PAGE_SIZES as readonly number[]).includes(pageSizeRaw)
    ? (pageSizeRaw as ReportMessagePageSize)
    : DEFAULT_REPORT_MESSAGE_PAGE_SIZE;
  const result: IncomingReportQuery = {
    search: (params.get("search") ?? "").trim().slice(0, 200),
    page: parsePositiveInt(params.get("page")) ?? 1,
    pageSize,
    sort: isSort(params.get("sort"))
      ? (params.get("sort") as ReportMessageSortName)
      : DEFAULT_REPORT_MESSAGE_SORT,
  };
  if (from != null && to != null) {
    result.from = from <= to ? from : to;
    result.to = from <= to ? to : from;
  }
  const platformRaw = params.get("platform");
  if (isPlatform(platformRaw)) result.platform = platformRaw;
  const endpointId = parsePositiveInt(params.get("endpointId"));
  if (endpointId != null) result.endpointId = endpointId;
  const messageTypeRaw = params.get("messageType");
  if (isMessageType(messageTypeRaw)) result.messageType = messageTypeRaw;
  return result;
}

export function incomingReportHref(query: IncomingReportQuery): string {
  const params = new URLSearchParams();
  if (query.from && query.to) {
    params.set("from", query.from);
    params.set("to", query.to);
  }
  if (query.search.trim().length > 0) params.set("search", query.search.trim());
  if (query.platform) params.set("platform", query.platform);
  if (query.endpointId != null) params.set("endpointId", String(query.endpointId));
  if (query.messageType) params.set("messageType", query.messageType);
  if (query.page > 1) params.set("page", String(query.page));
  if (query.pageSize !== DEFAULT_REPORT_MESSAGE_PAGE_SIZE) {
    params.set("pageSize", String(query.pageSize));
  }
  if (query.sort !== DEFAULT_REPORT_MESSAGE_SORT) params.set("sort", query.sort);
  const qs = params.toString();
  return qs.length > 0 ? `/reports/incoming?${qs}` : "/reports/incoming";
}

export function hasActiveIncomingFilters(query: IncomingReportQuery): boolean {
  if (query.search.trim().length > 0) return true;
  if (query.from != null || query.to != null) return true;
  if (query.platform) return true;
  if (query.endpointId != null) return true;
  if (query.messageType) return true;
  return false;
}

export function clearIncomingFilters(query: IncomingReportQuery): IncomingReportQuery {
  return {
    search: "",
    page: 1,
    pageSize: query.pageSize,
    sort: query.sort,
  };
}
